import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FeedbackEvent } from '@bonusly/common-feedback';
import {
	Avatar,
	Badge,
	Box,
	ChatAltOutline,
	ClappingHandsOutline,
	DotsHorizontalSolid,
	HistoryOutline,
	HStack,
	IconButton,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Portal,
	rem,
	TargetOutline,
	Text,
	Tooltip,
	useDisclosure,
	VStack,
} from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { Permission, ProtectedContent, usePermission } from '@bonusly/who-am-i';

import { CreateReviewModal } from '../../../../components/growth/review-recap/components/create-review-modal';
import { GiveBoxEvent } from '../../../../components/recognition/give-box/events';
import { AnalyticsEvent } from '../../../../lib/analytics/events';
import { useAnalytics } from '../../../../lib/analytics/use-analytics';
import { useCreateGoalModal } from '../../../../modules/growth/components/goals';
import { useDaysSinceLastInteraction } from '../../../bonus-feed/features/recognition/components/tracker-widget/hooks/use-interaction';
import { UserNavLink } from '../user-nav-link';
import { EnrichedDirectReport } from './direct-report-list';

interface DirectReportRowProps {
	directReport: EnrichedDirectReport;
}

export const DirectReportRow: FC<DirectReportRowProps> = ({ directReport }) => {
	const { t } = useTranslation(['navigation', 'tracker_widget', 'one_on_ones', 'common']);
	const { track } = useAnalytics();
	const canAccessGoals = usePermission(Permission.CanAccessGoals);
	const dispatch = useDispatch();

	// Modals
	const createReviewModal = useDisclosure();
	const { onOpen: onOpenGoalModal } = useCreateGoalModal();

	const handleRecognize = () => {
		track(AnalyticsEvent.TeamPanel.DirectReportActionClicked, { directReportId: directReport.id, action: 'Give Recognition' });
		dispatch(GiveBoxEvent.Open, {
			recipient: { id: directReport.id, username: directReport.username },
		});
	};

	const handleGiveFeedback = () => {
		track(AnalyticsEvent.TeamPanel.DirectReportActionClicked, { directReportId: directReport.id, action: 'Share Advice' });
		dispatch(FeedbackEvent.OpenUnsolicited, {
			defaultUsers: [
				{
					id: directReport.id,
					username: directReport.username,
				},
			],
		});
	};

	// Use enriched data from team tracker (same data source as non-manager view)
	const daysSinceInteraction = useDaysSinceLastInteraction(directReport.lastRecognizedAt);

	const lastRecognizedText = useMemo(() => {
		if (!directReport.lastRecognizedAt) {
			return t('tracker_widget:person_list.never_interacted');
		}
		if (daysSinceInteraction === 0) {
			return t('tracker_widget:person_list.last_interacted_today');
		}
		return t('tracker_widget:person_list.last_interacted', { count: daysSinceInteraction });
	}, [directReport.lastRecognizedAt, daysSinceInteraction, t]);

	return (
		<>
			<HStack justifyContent="space-between" alignItems="center" w="100%" position="relative">
				<UserNavLink
					to={`/team/${directReport.id}`}
					onClick={() => track(AnalyticsEvent.TeamPanel.LinkClicked, { link: 'Team Member', directReportId: directReport.id })}
					data-testid={`nav-link-view-${directReport.id}`}
				>
					<HStack spacing={rem(12)} alignItems="center">
						<Box position="relative">
							<Avatar name={directReport.displayName} src={directReport.profilePic} size="sm" />
							{directReport.recognitionCount > 0 && (
								<Tooltip
									label={`${t('tracker_widget:person_list.tooltip_title')} ${t('tracker_widget:person_list.recognition_count_tooltip', { count: directReport.recognitionCount })}`}
									hasArrow
									placement="top"
									maxW="fit-content"
								>
									<Badge
										bgColor="bg.subtle"
										borderColor="text.onColor"
										borderRadius="full"
										cursor="default"
										h={rem(18)}
										position="absolute"
										right="-5px"
										textAlign="center"
										top="-5px"
										w={rem(18)}
									>
										<Text color="text.soft" fontSize={1} fontWeight="semibold" w={rem(18)} position="absolute" right="0px">
											{directReport.recognitionCount}
										</Text>
									</Badge>
								</Tooltip>
							)}
						</Box>
						<VStack alignItems="flex-start" spacing={0}>
							<Text fontSize={100} fontWeight="medium" color="text.default" lineHeight={6}>
								{directReport.displayName}
							</Text>
							<Text fontSize={50} fontWeight="normal" color="text.softest" lineHeight={5}>
								{lastRecognizedText}
							</Text>
						</VStack>
					</HStack>
				</UserNavLink>

				{/* Actions menu - positioned outside the link for proper accessibility */}
				<Box position="absolute" right={rem(10)}>
					<Menu placement="right-start">
						<MenuButton
							as={IconButton}
							aria-label={t('navigation:teamMenu.menuLabel')}
							variant="ghost"
							size="sm"
							icon={<DotsHorizontalSolid size="sm" />}
							onClick={() => track(AnalyticsEvent.TeamPanel.DirectReportActionMenuOpened, { directReportId: directReport.id })}
							_hover={{ backgroundColor: 'secondary.bgAlpha' }}
							_active={{ backgroundColor: 'secondary.bgAlpha' }}
							_focusVisible={{ outline: '2px solid', outlineColor: 'border.focus', borderRadius: 'md', outlineOffset: '-2px' }}
						/>
						<Portal>
							<MenuList
								width="fit-content"
								zIndex={99999}
								px={rem(6)}
								py={rem(8)}
								sx={{
									'& > *:not(:last-child)': { mb: rem(4) },
									'& [role="menuitem"]': {
										minHeight: rem(40),
									},
									'& [role="menuitem"]:focus-visible': {
										outline: '2px solid',
										outlineColor: 'border.focus',
										outlineOffset: '-2px',
										borderRadius: 'sm',
										backgroundColor: 'bg.surface',
									},
								}}
							>
								{/* Give recognition */}
								<ProtectedContent permissions={Permission.CanGiveRecognition}>
									<MenuItem onClick={handleRecognize}>
										<HStack gap={2}>
											<ClappingHandsOutline />
											<Text>{t('navigation:teamMenu.giveRecognition')}</Text>
										</HStack>
									</MenuItem>
								</ProtectedContent>

								{/* Create goal */}
								<ProtectedContent permissions={Permission.CanAccessGoals}>
									{canAccessGoals && (
										<MenuItem
											onClick={() => {
												track(AnalyticsEvent.TeamPanel.DirectReportActionClicked, { directReportId: directReport.id, action: 'Create Goal' });
												onOpenGoalModal(directReport.id);
											}}
										>
											<HStack gap={2}>
												<TargetOutline />
												<Text>{t('navigation:teamMenu.createGoal')}</Text>
											</HStack>
										</MenuItem>
									)}
								</ProtectedContent>

								{/* Share feedback */}
								<ProtectedContent permissions={Permission.CanAccessOneOnOnes}>
									<MenuItem onClick={handleGiveFeedback}>
										<HStack gap={2}>
											<ChatAltOutline />
											<Text>{t('navigation:teamMenu.shareFeedback')}</Text>
										</HStack>
									</MenuItem>
								</ProtectedContent>

								{/* Create custom recap */}
								<ProtectedContent permissions={Permission.CanAccessOneOnOnes}>
									<MenuItem
										onClick={() => {
											track(AnalyticsEvent.TeamPanel.DirectReportActionClicked, { directReportId: directReport.id, action: 'Create Custom Recap' });
											createReviewModal.onOpen();
										}}
									>
										<HStack gap={2}>
											<HistoryOutline />
											<Text>{t('navigation:teamMenu.createCustomRecap')}</Text>
										</HStack>
									</MenuItem>
								</ProtectedContent>
							</MenuList>
						</Portal>
					</Menu>
				</Box>
			</HStack>

			{/* Modals */}
			<CreateReviewModal userId={directReport.id} {...createReviewModal} />
		</>
	);
};
